/**
 * AURA questionnaire schema.
 *
 * Fourteen questions, sourced from the MATRIX-derived questionnaire PDF.
 * One step per question — multi-step wizard, Goal-Gradient Effect.
 *
 * The schema here is the authoritative shape consumed by:
 *   - the wizard step components (one per question)
 *   - the live preview panel (re-themes a canonical template)
 *   - the Convex `questionnaire.upsert` mutation
 *   - ORA's system prompt (Batch 8) reads the same option labels
 *
 * Treat this file as a contract — changing IDs requires migrating
 * existing questionnaire rows in Convex and updating ORA's prompt.
 */

// ─────────────────────────────────────────────────────────────────────────
//  Option types
// ─────────────────────────────────────────────────────────────────────────

export interface ChoiceOption<TValue extends string = string> {
  value: TValue;
  label: string;
  /** Sub-label rendered under the option in the wizard. */
  hint?: string;
}

// ─────────────────────────────────────────────────────────────────────────
//  Question 1 — Main goal
// ─────────────────────────────────────────────────────────────────────────

export const GOAL_OPTIONS: ChoiceOption[] = [
  { value: 'portfolio',     label: 'Portfolio',                    hint: 'I want to present my work' },
  { value: 'ecommerce',     label: 'E-commerce',                   hint: 'I want to sell products' },
  { value: 'marketing',     label: 'Marketing / business / service', hint: 'I want to reach more customers' },
  { value: 'product',       label: 'Promote a single product',      hint: 'A focused product launch page' },
  { value: 'event',         label: 'Event or booking',              hint: 'Like BookMyShow or MakeMyTrip' },
  { value: 'saas',          label: 'SaaS website',                  hint: 'Like Figma, Notion, Slack' },
  { value: 'education',     label: 'Educational / courses',         hint: 'Like Coursera or Udemy' },
  { value: 'community',     label: 'Community / membership',        hint: 'Like Reddit or Discord' },
  { value: 'informational', label: 'Informational / blog',          hint: 'Like Wikipedia' },
  { value: 'entertainment', label: 'Entertainment',                 hint: 'Streaming, fan sites, hubs' },
  { value: 'chatbot',       label: 'Chatbot interface',             hint: 'A conversational UI front-end' },
  { value: 'other',         label: 'Something else',                hint: 'Describe in the next field' },
];

// ─────────────────────────────────────────────────────────────────────────
//  Question 2 — Audience
// ─────────────────────────────────────────────────────────────────────────

export const AUDIENCE_OPTIONS: ChoiceOption[] = [
  { value: 'b2c',           label: 'General consumers (B2C)' },
  { value: 'b2b',           label: 'Businesses (B2B)' },
  { value: 'service-seekers', label: 'People looking for services' },
  { value: 'startups',      label: 'Startups & entrepreneurs' },
  { value: 'students',      label: 'Students / learners' },
  { value: 'professionals', label: 'Industry professionals' },
  { value: 'hobbyists',     label: 'Hobbyists / niche communities' },
  { value: 'existing',      label: 'Existing customers / members' },
];

export const AGE_GROUP_OPTIONS: ChoiceOption[] = [
  { value: 'children',  label: 'Children (under 13)' },
  { value: 'teenagers', label: 'Teenagers (13-19)' },
  { value: 'adults',    label: 'Adults (20+)' },
  { value: 'mixed',     label: 'Mixed' },
];

// ─────────────────────────────────────────────────────────────────────────
//  Question 3 — Style
// ─────────────────────────────────────────────────────────────────────────

export const LAYOUT_STYLE_OPTIONS: ChoiceOption[] = [
  { value: 'minimal',  label: 'Minimal',  hint: 'Clean, focused, lots of white space' },
  { value: 'balanced', label: 'Balanced', hint: 'Harmonious, organised, moderate density' },
  { value: 'maximal',  label: 'Maximal',  hint: 'Dense, detailed, information-rich' },
];

export const BRAND_TONE_OPTIONS: ChoiceOption[] = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly',     label: 'Friendly' },
  { value: 'playful',      label: 'Fun / playful' },
  { value: 'luxury',       label: 'Luxury / premium' },
  { value: 'futuristic',   label: 'Futuristic' },
];

// ─────────────────────────────────────────────────────────────────────────
//  Question 5 — Typography
// ─────────────────────────────────────────────────────────────────────────

export const TYPOGRAPHY_FAMILY_OPTIONS: ChoiceOption[] = [
  { value: 'sans-serif', label: 'Sans-serif',  hint: 'Modern & clean (e.g. Inter, Geist)' },
  { value: 'serif',      label: 'Serif',       hint: 'Classic & elegant (e.g. Lora, Playfair)' },
  { value: 'mono',       label: 'Monospace',   hint: 'Technical & precise (e.g. JetBrains Mono)' },
  { value: 'display',    label: 'Display serif', hint: 'Editorial & dramatic (e.g. Instrument Serif)' },
];

// ─────────────────────────────────────────────────────────────────────────
//  Question 6 — Colour
// ─────────────────────────────────────────────────────────────────────────

export interface ColorPalette {
  primary: string;
  secondary: string;
  accent: string;
  detail: string;
}

/**
 * Palette mode. Semantics stay fixed across modes:
 *   - `primary`   = ink / text colour
 *   - `secondary` = page background colour
 * The mode flag tells LOOM/FORGE which "feel" the user wanted, so the
 * generated site sets `<html class="dark">` and friends accordingly.
 * Defaults to `'light'` for backward compatibility with rows persisted
 * before this field existed.
 */
export type PaletteMode = 'light' | 'dark';

export const PALETTE_MODE_OPTIONS: ChoiceOption<PaletteMode>[] = [
  { value: 'light', label: 'Light',  hint: 'Dark text on a light background' },
  { value: 'dark',  label: 'Dark',   hint: 'Light text on a dark background' },
];

export const DEFAULT_PALETTE: ColorPalette = {
  primary: '#15130F',
  secondary: '#F5F1E8',
  accent: '#B88817',
  detail: '#3F5F47',
};

export const DEFAULT_DARK_PALETTE: ColorPalette = {
  primary: '#F2EEE4',   // ink → near-white
  secondary: '#0E0D0B', // page → near-black
  accent: '#D4A84B',
  detail: '#7AA486',
};

/**
 * Presets are tagged with their `mode` so the colour step can filter the
 * shown row to whichever mode the user picked. Names are unique across
 * modes so a user-entered preset name can be looked up unambiguously.
 *
 * Mode invariant: `primary` is always darker than `secondary` for `light`
 * presets, lighter for `dark` presets. All preset pairs pass WCAG AA
 * (≥ 4.5:1) — verified by hand.
 */
export const PRESET_PALETTES: {
  name: string;
  mode: PaletteMode;
  palette: ColorPalette;
}[] = [
  // ── Light ────────────────────────────────────────────────────────────
  { name: 'Editorial', mode: 'light', palette: { primary: '#15130F', secondary: '#F5F1E8', accent: '#B88817', detail: '#3F5F47' } },
  { name: 'Cobalt',    mode: 'light', palette: { primary: '#0B1F3A', secondary: '#FFFFFF', accent: '#3B82F6', detail: '#0EA5E9' } },
  { name: 'Forest',    mode: 'light', palette: { primary: '#1B2A1B', secondary: '#F4F6F0', accent: '#5C8A4A', detail: '#A47C2A' } },
  { name: 'Rose',      mode: 'light', palette: { primary: '#2D1B22', secondary: '#FCEFEF', accent: '#C75D7C', detail: '#7A4A55' } },
  { name: 'Slate',     mode: 'light', palette: { primary: '#1C2024', secondary: '#F1F3F5', accent: '#FF6B35', detail: '#4A5568' } },
  { name: 'Sand',      mode: 'light', palette: { primary: '#3B2A18', secondary: '#FBF6EE', accent: '#D17A22', detail: '#7B5430' } },
  // ── Dark ─────────────────────────────────────────────────────────────
  { name: 'Midnight',  mode: 'dark',  palette: { primary: '#F2EEE4', secondary: '#0E0D0B', accent: '#D4A84B', detail: '#7AA486' } },
  { name: 'Carbon',    mode: 'dark',  palette: { primary: '#EAEAEA', secondary: '#121212', accent: '#FF6B35', detail: '#7C8794' } },
  { name: 'Ink',       mode: 'dark',  palette: { primary: '#E6ECF3', secondary: '#0A1322', accent: '#7CC5FF', detail: '#5AA3D9' } },
];

// ─────────────────────────────────────────────────────────────────────────
//  Question 7 — Border style
// ─────────────────────────────────────────────────────────────────────────

export const BORDER_SHAPE_OPTIONS: ChoiceOption[] = [
  { value: 'squared',  label: 'Squared',  hint: 'Sharp 90° corners' },
  { value: 'rounded',  label: 'Rounded',  hint: 'Soft 8-12px radius' },
  { value: 'beveled',  label: 'Beveled',  hint: 'Cut corners, geometric' },
];

export const BORDER_LINE_OPTIONS: ChoiceOption[] = [
  { value: 'solid',  label: 'Solid' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'dotted', label: 'Dotted' },
  { value: 'none',   label: 'None',   hint: 'Borderless — rely on spacing & tone' },
];

// ─────────────────────────────────────────────────────────────────────────
//  Question 9 — Top-nav link count
// ─────────────────────────────────────────────────────────────────────────

export const NAV_LINK_COUNT_OPTIONS: ChoiceOption[] = [
  { value: 'small',  label: '1–4 links',     hint: 'Simple navigation' },
  { value: 'medium', label: '5–6 links',     hint: 'Standard website' },
  { value: 'large',  label: '7 or more',     hint: 'Large website with sub-nav likely' },
];

// ─────────────────────────────────────────────────────────────────────────
//  Question 10 — Footer contents (multi-select)
// ─────────────────────────────────────────────────────────────────────────

export const FOOTER_OPTIONS: ChoiceOption[] = [
  { value: 'copyright',     label: 'Copyright notice' },
  { value: 'privacy_terms', label: 'Privacy policy & terms' },
  { value: 'contact',       label: 'Contact information' },
  { value: 'address',       label: 'Company address / location' },
  { value: 'about',         label: 'Brand description' },
  { value: 'certifications', label: 'Certifications / partner badges' },
  { value: 'payment_methods', label: 'Payment methods (e-commerce)' },
  { value: 'recent_blog',   label: 'Latest blog posts / resources' },
  { value: 'attribution',   label: 'Branding / attribution text' },
  { value: 'social_links',  label: 'Social media links' },
  { value: 'newsletter',    label: 'Newsletter signup' },
  { value: 'quick_links',   label: 'Quick links to main pages' },
  { value: 'extra_nav',     label: 'Extra navigation links' },
];

// ─────────────────────────────────────────────────────────────────────────
//  Question 12 — Landing page contents (multi-select)
// ─────────────────────────────────────────────────────────────────────────

export const LANDING_CONTENT_OPTIONS: ChoiceOption[] = [
  { value: 'hero',         label: 'Hero section (image/video + title)' },
  { value: 'product_highlights', label: 'Product or service highlights' },
  { value: 'features',     label: 'Features / benefits' },
  { value: 'testimonials', label: 'Testimonials / reviews' },
  { value: 'client_logos', label: 'Client logos / partners' },
  { value: 'stats',        label: 'Statistics or achievements' },
  { value: 'about',        label: 'About / brand story' },
  { value: 'team',         label: 'Team section' },
  { value: 'blog',         label: 'Blog or latest articles' },
  { value: 'faq',          label: 'FAQ section' },
  { value: 'pricing',      label: 'Pricing section' },
  { value: 'cta',          label: 'Call-to-action section' },
  { value: 'contact',      label: 'Contact section' },
  { value: 'newsletter',   label: 'Newsletter signup' },
];

// ─────────────────────────────────────────────────────────────────────────
//  Question 13 — Navigation importance
// ─────────────────────────────────────────────────────────────────────────

export const NAV_IMPORTANCE_OPTIONS: ChoiceOption[] = [
  { value: 'very_important', label: 'Very important', hint: 'Users should easily browse many pages' },
  { value: 'balanced',       label: 'Balanced',       hint: 'Navigation and content equally important' },
  { value: 'less_important', label: 'Less important', hint: 'Focus mainly on scrolling content' },
  { value: 'minimal',        label: 'Minimal',        hint: 'Single-page experience' },
  { value: 'unsure',         label: 'Not sure' },
];

// ─────────────────────────────────────────────────────────────────────────
//  Question 14 — Additional features (multi-select)
// ─────────────────────────────────────────────────────────────────────────

export const FEATURE_OPTIONS: ChoiceOption[] = [
  { value: 'ecommerce_checkout', label: 'E-commerce checkout' },
  { value: 'contact_forms',      label: 'Contact forms' },
  { value: 'cms',                label: 'Blog / content management' },
  { value: 'multi_language',     label: 'Multi-language support' },
  { value: 'accessibility',      label: 'Accessibility features (extra)' },
  { value: 'seo',                label: 'SEO optimisation' },
  { value: 'analytics',          label: 'Analytics integration' },
  { value: 'newsletter',         label: 'Newsletter signup' },
  { value: 'social_integration', label: 'Social media integration' },
  { value: 'none',               label: 'None of the above' },
];

// ─────────────────────────────────────────────────────────────────────────
//  Aggregate answer shape
// ─────────────────────────────────────────────────────────────────────────

/**
 * The full answer document persisted in Convex's `questionnaireAnswers.answers`.
 * Every field is optional in transit (the wizard autosaves between steps);
 * ORA validates completeness before consuming.
 */
export interface QuestionnaireAnswers {
  // Q1
  goal?: string;
  goal_other?: string;

  // Q2
  audience?: string[];
  audience_other?: string;
  age_groups?: string[];

  // Q3
  layout_style?: string;
  brand_tone?: string;

  // Q4
  inspiration_links?: string[];

  // Q5
  typography_family?: string;
  typography_preferred_fonts?: string;

  // Q6
  palette?: ColorPalette;
  palette_mode?: PaletteMode;
  palette_links?: string;

  // Q7
  border_shape?: string;
  border_line?: string;

  // Q8
  brand_info?: string;

  // Q9
  nav_link_count?: string;

  // Q10
  footer_items?: string[];

  // Q11
  page_count?: number;

  // Q12
  landing_content?: string[];

  // Q13
  nav_importance?: string;

  // Q14
  features?: string[];
}

// ─────────────────────────────────────────────────────────────────────────
//  Step manifest
// ─────────────────────────────────────────────────────────────────────────

export interface StepDefinition {
  id: string;
  index: number;
  /** Section title — appears as a small caps marginalia. */
  section: string;
  /** Display title — large display serif on the step. */
  title: string;
  /** One-line lead under the title. */
  lead: string;
  /** Whether this step has a default value and can be safely skipped. */
  optional: boolean;
}

export const STEPS: StepDefinition[] = [
  { id: 'goal',          index: 1,  section: 'goal',          title: 'What are you building?',                lead: 'The single sentence that describes your site.', optional: false },
  { id: 'audience',      index: 2,  section: 'audience',      title: 'Who is it for?',                        lead: 'Pick the audiences that will visit most.',       optional: false },
  { id: 'style',         index: 3,  section: 'style',         title: 'How should it feel?',                   lead: 'Density of layout, character of brand.',         optional: false },
  { id: 'inspiration',   index: 4,  section: 'inspiration',   title: 'Which sites inspire you?',              lead: 'Up to three links. Optional but useful.',        optional: true },
  { id: 'typography',    index: 5,  section: 'typography',    title: 'How should it read?',                   lead: 'Pick a family, or specify exact fonts.',         optional: false },
  { id: 'color',         index: 6,  section: 'colour',        title: 'What is the colour story?',             lead: 'Pick a preset, or set four colours by hand.',    optional: false },
  { id: 'border',        index: 7,  section: 'border',        title: 'What shape are the edges?',             lead: 'Squared, rounded, or beveled.',                  optional: false },
  { id: 'brand',         index: 8,  section: 'brand',         title: 'Tell us about the brand.',              lead: 'A short sentence ORA can use as voice.',         optional: true  },
  { id: 'navigation',    index: 9,  section: 'navigation',    title: 'How many top-nav links?',               lead: 'Hick\'s Law applies — fewer is faster.',         optional: false },
  { id: 'footer',        index: 10, section: 'footer',        title: 'What goes in the footer?',              lead: 'Pick everything you want at the bottom.',        optional: false },
  { id: 'pages',         index: 11, section: 'pages',         title: 'How many pages?',                       lead: 'AURA caps at five in this release.',               optional: false },
  { id: 'landing',       index: 12, section: 'landing',       title: 'What goes on the landing page?',        lead: 'Pick the sections you want above & below the hero.', optional: false },
  { id: 'nav-importance', index: 13, section: 'navigation',   title: 'How important is navigation?',          lead: 'Versus the content itself.',                     optional: false },
  { id: 'features',      index: 14, section: 'features',      title: 'Anything else?',                        lead: 'Extra features beyond what the sketches show.',  optional: true  },
];
